import React from 'react';
import { RevoltGlassButton, RevoltInput, RevoltTable, RevoltTableCell, DropdownSelect } from '../../../components';
import { logger } from '../../../core';
import './AccessCard.css';

export function AccessCard({
  members = [],
  onInvite,
  onRoleChange,
  onRemove,
  onTransferOwnership,
  page = 1,
  pageSize = 15,
  totalPages = 1,
  onPageChange = () => { },
  onPageSizeChange = () => { },
  canManage = false,
  isOwner = false,
  onRefresh = null,
  transferringMembers = new Set()
}) {
  const rows = members && members.length ? members : placeholderMembers;
  const [inviteEmail, setInviteEmail] = React.useState('');
  const [inviteRole, setInviteRole] = React.useState('member');

  const isTransferring = (member) => transferringMembers.has(member.userId);

  const handleInvite = () => {
    const payload = {
      email: inviteEmail.trim(),
      role: inviteRole
    };
    // For now, just log payload
    logger.log({
      tags: 'app|settings|access',
      color1: 'green'
    }, '[AccessCard] invite payload', payload);
    if (onInvite) {
      onInvite(payload);
    }
  };

  return (
    <div className="account-card settings-card access-card">
      <div className="card-header">
        <h2 className="card-title">
          <i className="fa-solid fa-users"></i> Membership
        </h2>
      </div>
      <div className="card-body access-body">
        {canManage ? (
          <>
            <div className="members-header">
              <div>
                <div className="members-title">Members & Roles</div>
                <div className="members-subtitle">Owners/admins manage access.</div>
              </div>
            </div>

            <div className="invite-form">
              <RevoltInput
                placeholder="Email to invite"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
              <div className="invite-row">
                <DropdownSelect
                  value={inviteRole}
                  options={[
                    { value: 'member', label: 'Member' },
                    { value: 'admin', label: 'Admin' },
                  ]}
                  onValueChange={(val) => setInviteRole(val)}
                  className="invite-role-select"
                />
                <RevoltGlassButton
                  variant="accent"
                  size="sm"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim()}
                  title="Invite user"
                >
                  Invite
                </RevoltGlassButton>
              </div>
            </div>

            <div className="members-table">
              <RevoltTable
                headers={["Email", "Username", "Role", "Status", "Actions"]}
                gridColumns={[1.4, 1.1, 0.7, 0.7, 0.9]}
                rows={rows.map((m, idx) => ([
                  <span className="members-email">{m.email || '—'}</span>,
                  <span className="members-name">{m.username || m.name || '—'}</span>,
                  <span className={`members-role ${m.role}`}>
                    {m.role === 'site_admin' ? 'site admin' : m.role}
                  </span>,
                  <span className="members-status">{m.status}</span>,
                  <RevoltTableCell>
                    <div className="members-actions">
                      {m.role !== 'owner' ? (
                        <>
                          {m.role !== 'admin' ? (
                            <button
                              className="member-action"
                              onClick={() => onRoleChange && onRoleChange(m, 'admin')}
                              disabled={!onRoleChange}
                              title="Promote to admin"
                            >
                              <i className="fa-solid fa-user-shield" aria-label="Make admin"></i>
                            </button>
                          ) : (
                            <>
                              <button
                                className="member-action secondary"
                                onClick={() => onRoleChange && onRoleChange(m, 'member')}
                                disabled={!onRoleChange}
                                title="Revoke admin"
                              >
                                <i className="fa-solid fa-user-slash" aria-label="Revoke admin"></i>
                              </button>
                              {isOwner && (
                                <button
                                  className={`member-action transfer ${isTransferring(m) ? 'transferring' : ''}`}
                                  onClick={() => onTransferOwnership && !isTransferring(m) && onTransferOwnership(m)}
                                  disabled={!onTransferOwnership || isTransferring(m)}
                                  title={isTransferring(m) ? "Transferring ownership..." : "Transfer ownership"}
                                >
                                  <i className={`fa-solid fa-crown ${isTransferring(m) ? 'fa-spin' : ''}`} aria-label="Transfer ownership"></i>
                                </button>
                              )}
                            </>
                          )}
                          <button
                            className="member-action danger"
                            onClick={() => onRemove && onRemove(m)}
                            disabled={!onRemove}
                            title="Remove member"
                          >
                            <i className="fa-solid fa-trash" aria-label="Remove member"></i>
                          </button>
                        </>
                      ) : (
                        <span className="owner-label">
                          {m.role === 'site_admin' ? 'Site Admin' : 'Owner'}
                        </span>
                      )}
                    </div>
                  </RevoltTableCell>
                ]))}
              />
            </div>
            <div className="members-pagination">
              <div className="page-size">
                <span>Show</span>
                <DropdownSelect
                  value={pageSize}
                  options={[15, 25, 50, 100]}
                  onValueChange={(val) => onPageSizeChange(Number(val))}
                />
                <span>per page</span>
              </div>
              <div className="page-controls">
                <button
                  className="member-action secondary"
                  onClick={() => onPageChange(Math.max(1, page - 1))}
                  disabled={page <= 1}
                >
                  <i className="fa-solid fa-chevron-left" aria-label="Prev"></i>
                </button>
                <span className="page-indicator">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="member-action secondary"
                  onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                >
                  <i className="fa-solid fa-chevron-right" aria-label="Next"></i>
                </button>
                <button
                  className="member-action secondary refresh-action"
                  onClick={async () => {
                    if (typeof onRefresh === 'function') {
                      const btn = document.activeElement;
                      if (btn && btn.classList && btn.classList.add) {
                        btn.classList.add('refreshing');
                        setTimeout(() => btn.classList.remove('refreshing'), 650);
                      }
                      await onRefresh();
                    }
                  }}
                  title="Refresh members"
                >
                  <i className="fa-solid fa-rotate-right" aria-label="Refresh"></i>
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="members-locked">
            <div className="members-title">Members & Roles</div>
            <div className="members-subtitle">Members do not have access to viewing membership.</div>
          </div>
        )}
      </div>
    </div>
  );
}

const placeholderMembers = [
  { name: 'You', username: 'you', email: 'you@example.com', role: 'owner', status: 'active' },
  { name: 'Alex Admin', username: 'alex', email: 'alex@example.com', role: 'admin', status: 'active' },
  { name: 'Jamie Member', username: 'jamie', email: 'jamie@example.com', role: 'member', status: 'invited' },
];

